/**
 * Socket.io Client for Real-time Features
 * Manages WebSocket connections for live updates
 */

import { ITicket, ITicketComment } from '@hrms/shared/types';
import { io, Socket } from 'socket.io-client';


interface TicketUpdateEvent {
  ticketId: string;
  ticket: ITicket;
  changeType: 'status' | 'assignment' | 'comment' | 'update';
  userId?: string;
}

interface CommentEvent {
  ticketId: string;
  comment: ITicketComment;
}

interface NotificationEvent {
  id: string;
  type: 'ticket_assigned' | 'ticket_updated' | 'comment_added' | 'sla_breach';
  title: string;
  message: string;
  ticketId?: string;
  userId: string;
  timestamp: Date;
}

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  // Event listeners
  private listeners: { [event: string]: Function[] } = {};

  connect(token: string) {
    if (this.socket?.connected) {
      return;
    }

    this.socket = io(import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000', {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.setupEventListeners();
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('socket:connected');
    });

    this.socket.on('disconnect', (reason: string) => {
      this.isConnected = false;
      this.emit('socket:disconnected', reason);
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error);
      this.reconnectAttempts++;
      this.emit('socket:error', error);
    });

    // Ticket events
    this.socket.on('ticket:updated', (data: TicketUpdateEvent) => {
      this.emit('ticket:updated', data);
    });

    this.socket.on('ticket:comment', (data: CommentEvent) => {
      this.emit('ticket:comment', data);
    });

    this.socket.on('ticket:assigned', (data: TicketUpdateEvent) => {
      this.emit('ticket:assigned', data);
    });

    this.socket.on('ticket:status_changed', (data: TicketUpdateEvent) => {
      this.emit('ticket:status_changed', data);
    });

    // Notification events
    this.socket.on('notification', (data: NotificationEvent) => {
      this.emit('notification', data);
    });

    // System events
    this.socket.on('system:announcement', (data: unknown) => {
      this.emit('system:announcement', data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners = {};
    }
  }

  // Event emitter methods
  on(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback?: Function) {
    if (!this.listeners[event]) return;

    if (callback) {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
    } else {
      delete this.listeners[event];
    }
  }

  private emit(event: string, data?: unknown) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(data));
    }
  }

  // Public emit method for external use
  emitToSocket(event: string, data?: unknown) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  // Room management
  joinTicketRoom(ticketId: string) {
    if (this.socket?.connected) {
      this.socket.emit('join:ticket', { ticketId });
    }
  }

  leaveTicketRoom(ticketId: string) {
    if (this.socket?.connected) {
      this.socket.emit('leave:ticket', { ticketId });
    }
  }

  joinUserRoom(userId: string) {
    if (this.socket?.connected) {
      this.socket.emit('join:user', { userId });
    }
  }

  // Send events
  sendTypingIndicator(ticketId: string, isTyping: boolean) {
    if (this.socket?.connected) {
      this.socket.emit('ticket:typing', { ticketId, isTyping });
    }
  }

  sendCommentUpdate(ticketId: string, comment: string) {
    if (this.socket?.connected) {
      this.socket.emit('ticket:comment_preview', { ticketId, comment });
    }
  }

  // Status getters
  get connected() {
    return this.isConnected;
  }

  get socketId() {
    return this.socket?.id;
  }
}

// Create singleton instance
export const socketService = new SocketService();

// React hook for using socket events
export const useSocket = () => {
  const on = (event: string, callback: Function) => {
    socketService.on(event, callback);
  };

  const off = (event: string, callback?: Function) => {
    socketService.off(event, callback);
  };

  const emit = (event: string, data?: unknown) => {
    socketService.emitToSocket(event, data);
  };

  return {
    on,
    off,
    emit,
    connected: socketService.connected,
    socketId: socketService.socketId,
    joinTicketRoom: socketService.joinTicketRoom.bind(socketService),
    leaveTicketRoom: socketService.leaveTicketRoom.bind(socketService),
    joinUserRoom: socketService.joinUserRoom.bind(socketService),
    sendTypingIndicator: socketService.sendTypingIndicator.bind(socketService),
  };
};

export default socketService;
